/* @flow */

import React from 'react';

import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Alert from '@material-ui/lab/Alert';
import EditOutlined from '@material-ui/icons/EditOutlined';
import IconButton from '@material-ui/core/IconButton';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TablePagination from '@material-ui/core/TablePagination';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Tooltip from '@material-ui/core/Tooltip';
import BusinessIcon from '@material-ui/icons/Business';

import SecurityComponent from 'components/security';
import StyledTableCellTextComponent from 'components/table/cellText';
import StyledTableHeadComponent from 'components/table/head';
import StyledTableRowComponent from 'components/table/row';
import CountryFieldSellersComponent from 'components/formFields/countries/sellers';
import IntlMessageComponent from 'components/formatters/intlMessage';
import PageSubTitleViewComponent from 'components/pageTitle/subTitle';
import ButtonComponent from 'components/buttons/button';
import UploadFileFieldComponent from 'components/formFields/upload/index';
import TooltipComponent from 'components/toolTips/index';
import FormErrorsComponent from 'components/formErrors';

import SupplierCompany from 'models/confirming/supplierCompany';
import CompanyContactPerson from 'models/companies/companyContactPerson';

type ConfirmingSuppliersDetailsViewComponentProps = {
  supplierCompanyDetails: SupplierCompany;
  form: any;
  isProcessing: boolean;
  document: Document;
  salesMandateDocument: Document;
  errors: Map<string,?string>;
  formErrors: string[];
  handleChange: () => void;
  handleTypeChange: () => void;
  updateBankAccount: () => void;
  onCancel: () => void;
  handleDocumentSelect: (file: File) => void;
  handleDocumentSelectFailure: (file: File) => void;
  handleDocumentRemove: () => void;
  handleSalesMandateDocumentSelect: (file: File) => void;
  handleSalesMandateDocumentSelectFailure: (file: File) => void;
  handleSalesMandateDocumentRemove: () => void;
  downloadSalesMandate: () => void;
}

const columnData = [
  { id: 'lastName', translationKey: 'component.users.list.header.lastName' },
  { id: 'firstName', translationKey: 'component.users.list.header.firstName' },
  { id: 'functionName', translationKey: 'component.users.list.header.functionName' }
];

export default function ConfirmingSuppliersDetailsViewComponent(props: ConfirmingSuppliersDetailsViewComponentProps) {

  const newUser = () => props.handleNewUser();
  const editUser = (id: number) => props.handleEditUser(id);

  return (
    <React.Fragment>
      <Paper elevation={0}>

        <Box mt={5} pt={3} pl={3} pr={3}>

          <FormErrorsComponent formErrors={props.formErrors} />
          <Grid container direction="row" justify="flex-start">
            <Grid item xs={6}>
              <PageSubTitleViewComponent id="component.confirming.supplierdetails.companyinformation" />
              <Grid container direction="row" justify="flex-start" alignItems="center">
                <Grid item alignItems="center">
                  <Box p={2}>
                    <BusinessIcon color="primary" fontSize="large" />
                  </Box>
                </Grid>
                <Grid item alignItems="center">
                  <Box pt={1} pb={2} textAlign="left">
                    <Typography variant="body1" gutterBottom>
                      <b>{props.supplierCompanyDetails.supplierName}</b>
                    </Typography>

                    <Typography variant="body1" gutterBottom>
                      {props.supplierCompanyDetails.vatNbr}
                    </Typography>

                    <Typography variant="body1" gutterBottom>
                      {props.supplierCompanyDetails.address}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

            </Grid>

          </Grid>
        </Box>
        <Box p={3}>
          <Grid container direction="row" justify="flex-start" alignItems="flex-start" spacing={1}>
            <Grid item xs={12} md={6}>
              <PageSubTitleViewComponent id="component.confirming.supplierdetails.bankinformation" />
              <Box px={4}>
                <CountryFieldSellersComponent
                  id="form.bankCountry"
                  label={<IntlMessageComponent id="form.bankCountry.label" />}
                  error={!!props.errors.get('bankCountryId')}
                  helperText={props.errors.get('bankCountryId') ?
                    <IntlMessageComponent id={props.errors.get('bankCountryId')} /> : ''}
                  value={props.form.bankCountryId}
                  changeHandler={props.handleTypeChange('bankCountryId')}
                  fullWidth
                  margin="normal"
                />
              </Box>
            </Grid>
          </Grid>

          <Grid container direction="row" justify="flex-start" alignItems="flex-start" spacing={1}>
            <Grid item xs={12} md={6}>
              <Box px={4}>
                <TextField
                  id="form.bic"
                  data-walkme="subscription-field-bic"
                  label={<IntlMessageComponent id="form.bankIdentifier.label" />}
                  error={!!props.errors.get('bankIdentifier')}
                  helperText={(props.errors.get('bankIdentifier')) ?
                    <IntlMessageComponent id={props.errors.get('bankIdentifier')} /> : ''}
                  value={props.form.bankIdentifier}
                  onChange={props.handleChange('bankIdentifier')}
                  fullWidth
                  margin="normal"
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box px={4}>
                <TextField
                  id="form.iban"
                  data-walkme="subscription-field-iban"
                  label={<IntlMessageComponent id="form.bankAccount.label" />}
                  error={!!props.errors.get('bankAccount')}
                  helperText={(props.errors.get('bankAccount')) ?
                    <IntlMessageComponent id={props.errors.get('bankAccount')} /> : ''}
                  value={props.form.bankAccount}
                  onChange={props.handleChange('bankAccount')}
                  fullWidth
                  margin="normal"
                />
              </Box>
            </Grid>
          </Grid>

          {!props.supplierCompanyDetails.hasBankAccountDocument && <Grid container direction="row" justify="flex-start" alignItems="flex-start" spacing={1}>
            <Grid item xs={12} md={6}>
              <Box px={4}>
                <Box pageName="Tooltip.CompanyInfo.BankAccount" elementName={"document." + props.document.code}>
                  <TooltipComponent boxWidth={"100%"} pageName="Tooltip.CompanyInfo.BankAccount" elementName={"document." + props.document.code}>
                    <UploadFileFieldComponent
                      document={props.document}
                      successHandler={props.handleDocumentSelect}
                      failHandler={props.handleDocumentSelectFailure}
                      removeHandler={props.handleDocumentRemove}
                    />
                  </TooltipComponent>
                </Box>
              </Box>
            </Grid>
          </Grid>}
          {props.supplierCompanyDetails.salesMandateDocumentId <= 0 &&
            <Grid container direction="row" justify="flex-start" alignItems="flex-start" spacing={1}>
              <Grid item xs={12} md={6}>
                <Box pt={5}>
                  <PageSubTitleViewComponent id="component.confirming.supplierdetails.salesmandate" />
                </Box>
                <Box px={4}>
                  <Box pageName="Tooltip.CompanyInfo.SalesMandate" elementName={"document." + props.document.code}>
                    <TooltipComponent pageName="Tooltip.CompanyInfo.SalesMandate" elementName={"document." + props.document.code}>
                      <UploadFileFieldComponent
                        document={props.salesMandateDocument}
                        successHandler={props.handleSalesMandateDocumentSelect}
                        failHandler={props.handleSalesMandateDocumentSelectFailure}
                        removeHandler={props.handleSalesMandateDocumentRemove}
                      />
                    </TooltipComponent>
                  </Box>
                </Box>
              </Grid>
            </Grid>}
          {props.supplierCompanyDetails.salesMandateDocumentId > 0 && <React.Fragment>
            <Box pt={5}>
              <PageSubTitleViewComponent id="component.confirming.supplierdetails.salesmandate" />
            </Box>
            <Box p={4}>
              <ButtonComponent
                color="primary"
                variant="contained"
                isProcessing={props.isProcessing}
                onClick={props.downloadSalesMandate}
              ><IntlMessageComponent id="component.confirming.supplierdetails.downloadsalesmandate" /></ButtonComponent>
            </Box>
          </React.Fragment>}
        </Box>
        <Box p={5}>
          <ButtonComponent
            color="primary"
            variant="contained"
            isProcessing={props.isProcessing}
            onClick={props.updateBankAccount}
          ><IntlMessageComponent id="general.submit" /></ButtonComponent>
        </Box>
      </Paper>
      <Paper elevation={0}>
        <Box mt={5} pt={3} pl={3} pr={3}>

          <PageSubTitleViewComponent id="component.confirming.supplierdetails.contacts" />
          <Box pb={2}>
            <Alert severity="info">
              <IntlMessageComponent id="component.confirming.supplierdetails.contactalert" />
            </Alert>
          </Box>
          <Box p={3} component="div" overflow="auto">
            <Table>
              <StyledTableHeadComponent>
                <StyledTableRowComponent>
                  {columnData.map(column => (
                    <StyledTableCellTextComponent key={column.id} padding="none">
                      <IntlMessageComponent id={column.translationKey} />
                    </StyledTableCellTextComponent>
                  ))}
                  <StyledTableCellTextComponent padding="checkbox" />
                </StyledTableRowComponent>
              </StyledTableHeadComponent>

              <TableBody bgcolor="white">
                {props.supplierCompanyDetails.contactPersons.length === 0 &&
                  <StyledTableRowComponent>
                    <StyledTableCellTextComponent colSpan={columnData.length + 1}>
                      <Box textAlign="center">
                        <IntlMessageComponent id="general.noData" />
                      </Box>
                    </StyledTableCellTextComponent>
                  </StyledTableRowComponent>
                }

                {props.supplierCompanyDetails.contactPersons.map(user => (
                  <StyledTableRowComponent key={`companyUser-${user.id}`}>
                    <StyledTableCellTextComponent padding="none">
                      <b>{user.lastName}</b>
                    </StyledTableCellTextComponent>

                    <StyledTableCellTextComponent padding="none">
                      <b>{user.firstName}</b>
                    </StyledTableCellTextComponent>

                    <StyledTableCellTextComponent padding="none">
                      {user.functionName}
                    </StyledTableCellTextComponent>

                    <StyledTableCellTextComponent padding="checkbox">
                      <SecurityComponent pageName="POS.Users" elementName="btnEdit" checkCanManageUsers>
                        <Tooltip title={<IntlMessageComponent id="component.users.list.edit.tooltip" />}>
                          <IconButton onClick={() => editUser(user.id)}>
                            <EditOutlined />
                          </IconButton>
                        </Tooltip>
                      </SecurityComponent>

                    </StyledTableCellTextComponent>
                  </StyledTableRowComponent>
                ))}
              </TableBody>
            </Table>
          </Box>

          <Box px={2} py={4}>
            <SecurityComponent pageName="POS.Users" elementName="btnNew" checkCanManageUsers>
              <ButtonComponent
                color="primary"
                variant="contained"
                onClick={newUser}
              ><IntlMessageComponent id="component.ubo.list.actions.add" /></ButtonComponent>
            </SecurityComponent>
          </Box>
        </Box>
      </Paper>
    </React.Fragment >
  );
}
